import { Geometry } from "geojson";
import { useEffect, useRef } from "react";
import { MapRef } from "react-map-gl/maplibre";
import {
	TerraDraw,
	TerraDrawCircleMode,
	TerraDrawMapLibreGLAdapter,
	TerraDrawPointMode,
	TerraDrawPolygonMode,
	TerraDrawRectangleMode,
	TerraDrawSelectMode,
	GeoJSONStoreFeatures,
} from "terra-draw";

/**
 * Hook for integrating TerraDraw with MapLibre for spatial selection functionality.
 * Supports point, circle, rectangle, and polygon drawing modes.
 * Automatically saves drawn geometries to the Zustand store.
 * Ensures only one shape can be drawn at a time.
 */
export const useSpatialSelection = ({
	map,
	currentMode,
	onFinish,
	pointModeValidation,
}: {
	map: MapRef | undefined;
	currentMode: "point" | "circle" | "rectangle" | "polygon" | "select";
	onFinish: (geometry: Geometry | null) => void;
	pointModeValidation?: (features: GeoJSONStoreFeatures) => boolean;
}) => {
	const drawInstance = useRef<TerraDraw | null>(null);

	// Initialize TerraDraw when map is available
	useEffect(() => {
		if (!map) return;

		// Create TerraDraw instance with MapLibre adapter
		const draw = new TerraDraw({
			adapter: new TerraDrawMapLibreGLAdapter({
				map: map.getMap(),
				coordinatePrecision: 9,
			}),
			modes: [
				new TerraDrawPointMode({
					validation: (f) => pointModeValidation?.(f) ?? true,
				}),
				new TerraDrawCircleMode(),
				new TerraDrawRectangleMode(),
				new TerraDrawPolygonMode(),
				new TerraDrawSelectMode({
					flags: {
						arbitary: {
							feature: {
								draggable: true,
								rotateable: true,
								scaleable: true,
								coordinates: {
									midpoints: true,
									draggable: true,
									deletable: true,
								},
							},
						},
					},
				}),
			],
		});

		// Start the drawing instance
		draw.start();
		drawInstance.current = draw;

		// Set up event listeners for drawing completion
		draw.on("finish", (_id, context) => {
			if (["circle", "rectangle", "polygon"].includes(context.mode)) {
				// Get all features
				const features = draw.getSnapshot();

				if (features.length > 1) {
					// Remove all features except the last one (newest)
					features.slice(0, -1).forEach((feature) => {
						if (feature.id) draw.removeFeatures([feature.id]);
					});
				}
			}

			const features = draw.getSnapshot();

			if (features.length > 1) {
				onFinish({
					type: "MultiPoint",
					coordinates: features
						.map((feature) =>
							feature.geometry.type === "Point"
								? feature.geometry.coordinates
								: null,
						)
						.filter((coord) => coord !== null),
				});
			} else if (features.length === 1) {
				onFinish(features[0]!.geometry);
			} else {
				onFinish(null);
			}
		});

		return () => {
			// Cleanup on unmount
			if (drawInstance.current) {
				drawInstance.current.stop();
				drawInstance.current = null;
			}
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, pointModeValidation]);

	// Switch drawing modes based on currentSpatialType
	useEffect(() => {
		if (!drawInstance.current) return;

		const draw = drawInstance.current;

		// Clear any existing drawings to ensure only one shape at a time
		draw.clear();
		draw.setMode(currentMode);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentMode]);

	return {
		drawInstance: drawInstance.current,
	};
};
